import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { CChartBar } from '@coreui/react-chartjs';
import { CRow, CCol, CCard, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CWidgetStatsF } from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilLocationPin, cilChartPie } from '@coreui/icons';
import { subFiltersConfig } from './constants';
import { getCategoryConfig } from '../CategoryConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

const ExecutiveSummary = ({ data, mapFilter }) => {
  // 1. Filtrado de datos con mayor compatibilidad
  const senales = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    // Si es 'all' o no hay filtro específico, mostrar todo
    if (!mapFilter || mapFilter === 'all') return data;
    
    const filtered = data.filter(item => {
      if (!item) return false;
      const tipo = (item.tipo || '').toLowerCase();
      const tipoEsp = (item.tipo_especifico || '').toLowerCase();
      
      const config = getCategoryConfig(mapFilter);
      return config.regex.test(tipoEsp + tipo);
    });

    return filtered.length > 0 ? filtered : data;
  }, [data, mapFilter]);

  // 2. Ranking de Calles
  const topStreets = useMemo(() => {
    const streetCounts = {};
    if (!senales) return [];
    
    senales.forEach(item => {
      if (!item) return;
      let addr = (typeof item.direccion === 'string') ? item.direccion : 'Sin Ubicación';
      const parts = addr.split(',');
      let street = (parts[0] || '').trim();
      street = street.replace(/\d+/g, '').replace(/None/g, '').trim(); 
      if (street.length > 3) {
        streetCounts[street] = (streetCounts[street] || 0) + 1;
      }
    });

    return Object.entries(streetCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [senales]);

  // 3. Distribución por Localidad
  const localityStats = useMemo(() => {
    const localityCounts = {};
    if (!senales) return { labels: [], values: [] };

    senales.forEach(item => {
      if (!item) return;
      const address = item.direccion || '';
      const parts = address.split(',').map(p => p.trim()).filter(p => !!p);
      const skipKeywords = ['argentina', 'provincia', 'partido', 'buenos aires', '3 de febrero', 'tres de febrero', 'none'];
      let loc = 'Otras Áreas';
      
      if (parts.length >= 1) {
        for (let i = parts.length - 1; i >= 0; i--) {
          const pLower = parts[i].toLowerCase().trim();
          const isGeneric = skipKeywords.some(key => pLower.includes(key)) || pLower.length < 3;
          if (!isGeneric && (i > 0 || parts.length === 1)) {
            loc = parts[i].split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
            break;
          }
        }
      }
      if (loc && loc !== 'None') {
        localityCounts[loc] = (localityCounts[loc] || 0) + 1;
      }
    });

    const sorted = Object.entries(localityCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);
    return { labels: sorted.map(s => s[0]), values: sorted.map(s => s[1]) };
  }, [senales]);
  // 4. KPIs y Gráfico de Categorización Modular
  const stats = useMemo(() => {
    const topLocality = localityStats.labels[0] || 'N/A';
    const topStreet = topStreets[0]?.[0] || 'N/A';
    
    const catCounts = {};
    const config = getCategoryConfig(mapFilter);
    
    senales.forEach(s => {
      let subFound = null;
      
      // Buscamos si matchea algún sub-filtro configurado para esta categoría
      if (config.subFilters.length > 0) {
        const matchingSub = config.subFilters.find(sub => sub.check(s));
        if (matchingSub) subFound = matchingSub.label;
      }
      
      const finalKey = subFound || s.tipo_principal || s.tipo || 'Otras';
      catCounts[finalKey] = (catCounts[finalKey] || 0) + 1;
    });
    
    const sortedTypes = Object.entries(catCounts).sort((a,b) => b[1] - a[1]);
    const topType = sortedTypes[0]?.[0] || 'N/A';

    return { topLocality, topStreet, topType, sortedTypes, config };
  }, [senales, localityStats, topStreets, mapFilter]);

  const typeChartData = useMemo(() => {
    return {
      labels: stats.sortedTypes.map(t => t[0]),
      values: stats.sortedTypes.map(t => t[1])
    };
  }, [stats.sortedTypes]);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="p-4 text-center text-muted">No hay datos disponibles.</div>;
  }

  return (
    <div className="executive-summary-content">
      {/* KPI Cards Row */}
      <CRow className="mb-4 g-3">
        <CCol xs={12} md={4}>
          <CWidgetStatsF
            className="mb-3 border-0 shadow-sm text-truncate"
            icon={<CIcon icon={cilLocationPin} height={24} />}
            padding={false}
            title="Punto más Afectado (Calle)"
            value={stats.topStreet.length > 20 ? stats.topStreet.substring(0, 18) + '...' : stats.topStreet}
            color="danger"
            style={{ borderRadius: '15px' }}
          />
        </CCol>
        <CCol xs={12} md={4}>
          <CWidgetStatsF
            className="mb-3 border-0 shadow-sm text-truncate"
            icon={<CIcon icon={cilLocationPin} height={24} />}
            padding={false}
            title="Zona más Afectada"
            value={stats.topLocality.length > 20 ? stats.topLocality.substring(0, 18) + '...' : stats.topLocality}
            color="warning"
            style={{ borderRadius: '15px' }}
          />
        </CCol>
        <CCol xs={12} md={4}>
          <CWidgetStatsF
            className="mb-3 border-0 shadow-sm text-truncate"
            icon={<CIcon icon={cilChartPie} height={24} />}
            padding={false}
            title="Tipo Principal Predominante"
            value={stats.topType.length > 20 ? stats.topType.substring(0, 18) + '...' : stats.topType}
            color="primary"
            style={{ borderRadius: '15px' }}
          />
        </CCol>
      </CRow>

      <CRow className="g-4">
        {/* Gráfico de Localidades */}
        <CCol xs={12} xl={6}>
          <CCard className="h-100 border-0 shadow-sm" style={{ borderRadius: '15px' }}>
            <CCardBody className="p-4">
              <h6 className="fw-bold mb-4 d-flex align-items-center gap-2" style={{ color: '#131133' }}>
                <span style={{ width: '4px', height: '18px', background: '#3399ff', borderRadius: '10px' }}></span>
                DISTRIBUCIÓN POR LOCALIDAD
              </h6>
              <div style={{ height: '300px' }}>
                <CChartBar
                  data={{
                    labels: localityStats.labels,
                    datasets: [{ label: 'Incidentes', backgroundColor: '#321fdb', data: localityStats.values, borderRadius: 8 }],
                  }}
                  options={{
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: '#64748b' } },
                      y: { grid: { display: false }, ticks: { color: '#334155', font: { weight: '500' } } }
                    }
                  }}
                />
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        {/* Gráfico de Tipos Principales */}
        <CCol xs={12} xl={6}>
          <CCard className="h-100 border-0 shadow-sm" style={{ borderRadius: '15px' }}>
            <CCardBody className="p-4">
              <h6 className="fw-bold mb-4 d-flex align-items-center gap-2" style={{ color: '#131133' }}>
                <span style={{ width: '4px', height: '18px', background: '#2eb85c', borderRadius: '10px' }}></span>
                DISTRIBUCIÓN POR TIPO PRINCIPAL
              </h6>
              <div style={{ height: '300px' }}>
                <CChartBar
                  data={{
                    labels: typeChartData.labels,
                    datasets: [{ label: 'Cantidad', backgroundColor: '#2eb85c', data: typeChartData.values, borderRadius: 8 }],
                  }}
                  options={{
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: '#64748b' } },
                      y: { grid: { display: false }, ticks: { color: '#334155', font: { weight: '500' } } }
                    }
                  }}
                />
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        {/* Tabla de Calles Rankeadas */}
        <CCol xs={12}>
          <CCard className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
            <CCardBody className="p-4">
              <h6 className="fw-bold mb-4 d-flex align-items-center gap-2" style={{ color: '#131133' }}>
                <span style={{ width: '4px', height: '18px', background: '#6366f1', borderRadius: '10px' }}></span>
                RANKING DE PUNTOS CRÍTICOS (CALLES)
              </h6>
              <div className="table-responsive">
                <CTable hover align="middle" className="mb-0">
                  <CTableHead className="text-secondary text-uppercase small fw-bold">
                    <CTableRow>
                      <CTableHeaderCell className="bg-light border-0 py-3 rounded-start">#</CTableHeaderCell>
                      <CTableHeaderCell className="bg-light border-0 py-3">Calle / Tramo</CTableHeaderCell>
                      <CTableHeaderCell className="bg-light border-0 py-3 text-center">Cant.</CTableHeaderCell>
                      <CTableHeaderCell className="bg-light border-0 py-3 rounded-end">Impacto</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {topStreets.map(([street, count], index) => (
                      <CTableRow key={index}>
                        <CTableDataCell className="text-secondary fw-bold">{index + 1}</CTableDataCell>
                        <CTableDataCell className="fw-bold text-dark">{street}</CTableDataCell>
                        <CTableDataCell className="text-center">
                          <span className="badge bg-white text-dark border shadow-sm px-3 py-2" style={{ borderRadius: '8px' }}>{count}</span>
                        </CTableDataCell>
                        <CTableDataCell>
                          <div className="progress" style={{ height: '8px', background: '#f1f5f9', borderRadius: '10px' }}>
                            <div className="progress-bar" style={{ width: `${topStreets[0][1] > 0 ? (count / topStreets[0][1]) * 100 : 0}%`, backgroundColor: '#5856d6', borderRadius: '10px' }}></div>
                          </div>
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
};

export default ExecutiveSummary;
